import { useState, useEffect, useMemo } from 'react';
import { StoryChiefPost, BlogFilters } from '../types/blog';
import { storyChiefAPI } from '../services/storychiefApi';
import { htmlPersistence } from '../services/htmlPersistence';

// Add cache management for posts list
interface CacheEntry {
  data: StoryChiefPost[];
  lastModified: string;
  cachedAt: number;
}

const POSTS_CACHE_KEY = 'blog_posts_list';

class PostsCache {
  // Get the latest modification date from a list of posts
  private getLatestModificationDate(posts: StoryChiefPost[]): string {
    if (!posts || posts.length === 0) return new Date().toISOString();
    
    const dates = posts.map(post => 
      post.updated_at || post.published_at
    ).filter(Boolean);
    
    if (dates.length === 0) return new Date().toISOString();
    
    // Return the most recent date
    return dates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0];
  }

  // Cache posts list with modification tracking
  setCachedPosts(posts: StoryChiefPost[]): void {
    const latestModified = this.getLatestModificationDate(posts);
    const cacheEntry: CacheEntry = {
      data: posts,
      lastModified: latestModified,
      cachedAt: Date.now()
    };
    
    localStorage.setItem(POSTS_CACHE_KEY, JSON.stringify(cacheEntry));
    console.log('📦 Cached posts list with latest modification:', latestModified);
  }

  // Get cached posts if they're still valid
  async getCachedPosts(): Promise<StoryChiefPost[] | null> {
    try {
      const cached = localStorage.getItem(POSTS_CACHE_KEY);
      if (!cached) return null;

      const cacheEntry: CacheEntry = JSON.parse(cached);
      
      // For posts list, do a quick check by fetching just the first few posts to compare dates
      try {
        console.log('📦 Validating posts cache...');
        const response = await storyChiefAPI.getPosts({ per_page: 10, status: 'published' });
        const currentLatestModified = this.getLatestModificationDate(response.data);
        
        const cachedDate = new Date(cacheEntry.lastModified);
        const currentDate = new Date(currentLatestModified);
        
        if (cachedDate >= currentDate) {
          console.log('📦 Using cached posts list (content unchanged)');
          return cacheEntry.data;
        } else {
          console.log('📦 Posts cache invalid - content was modified');
          localStorage.removeItem(POSTS_CACHE_KEY);
          return null;
        }
      } catch (apiError) {
        console.warn('Could not verify posts cache validity, using cached version:', apiError);
        // Fallback to time-based cache
        const cacheAge = Date.now() - cacheEntry.cachedAt;
        const maxFallbackAge = 5 * 60 * 1000; // 5 minutes fallback for posts list
        
        if (cacheAge < maxFallbackAge) {
          console.log('📦 Using cached posts list (fallback)');
          return cacheEntry.data;
        } else {
          console.log('📦 Posts cache too old, will fetch fresh');
          return null;
        }
      }
    } catch (error) {
      console.error('Error reading cached posts:', error);
      return null;
    }
  }

  // Clear posts cache
  clearCache(): void {
    localStorage.removeItem(POSTS_CACHE_KEY);
    console.log('🗑️ Cleared posts cache');
  }
}

const postsCache = new PostsCache();

export const useBlogPosts = (filters: BlogFilters = {}) => {
  const [posts, setPosts] = useState<StoryChiefPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [meta, setMeta] = useState<Record<string, unknown> | null>(null);
  const [htmlStatus, setHtmlStatus] = useState<{ [key: string]: boolean }>({});

  // Memoize the filters to avoid unnecessary re-renders when the object reference changes
  const filtersString = useMemo(() => JSON.stringify(filters), [filters]);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try to get cached posts first
        const cachedPosts = await postsCache.getCachedPosts();
        if (cachedPosts) {
          setPosts(cachedPosts);
          
          // Check HTML file status for cached posts
          const statusChecks = await Promise.all(
            cachedPosts.map(async (post) => {
              const exists = await htmlPersistence.checkHTMLExists(post.slug);
              return { slug: post.slug, exists };
            })
          );

          const statusMap = statusChecks.reduce((acc, { slug, exists }) => {
            acc[slug] = exists;
            return acc;
          }, {} as { [key: string]: boolean });

          setHtmlStatus(statusMap);
          setLoading(false);
          return;
        }

        // Fetch fresh data from API
        console.log('📡 Fetching fresh posts from StoryChief API...');
        const response = await storyChiefAPI.getPosts(filters);
        setPosts(response.data);
        setMeta(response.meta);

        // Cache the fresh data
        postsCache.setCachedPosts(response.data);

        // Check HTML file status for each post (with staleness check)
        const statusChecks = await Promise.all(
          response.data.map(async (post) => {
            const exists = await htmlPersistence.checkHTMLExists(post.slug);
            return { slug: post.slug, exists };
          })
        );

        const statusMap = statusChecks.reduce((acc, { slug, exists }) => {
          acc[slug] = exists;
          return acc;
        }, {} as { [key: string]: boolean });

        setHtmlStatus(statusMap);

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch posts');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [filtersString, filters]); // Include both filtersString and filters for safety

  const generateHTML = async (slug: string) => {
    try {
      // Get the full post data
      const post = await storyChiefAPI.getPostBySlug(slug);
      
      // Generate and save HTML
      await htmlPersistence.savePostHTML(post);
      
      // Update HTML status
      setHtmlStatus(prev => ({ ...prev, [slug]: true }));
      
      console.log(`Generated HTML for post: ${post.title}`);
      return { success: true, post };
      
    } catch (error) {
      console.error(`Failed to generate HTML for ${slug}:`, error);
      throw error;
    }
  };

  const generateAllHTML = async () => {
    const results = { success: 0, errors: 0, details: [] as string[] };
    
    for (const post of posts) {
      try {
        await generateHTML(post.slug);
        results.success++;
        results.details.push(`✓ Generated: ${post.title}`);
      } catch (error) {
        results.errors++;
        results.details.push(`✗ Failed: ${post.title} - ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
    
    return results;
  };

  // Force refresh (bypass cache)
  const forceRefresh = async () => {
    postsCache.clearCache();
    // Re-trigger the effect by updating a dependency
    const response = await storyChiefAPI.getPosts(filters);
    setPosts(response.data);
    setMeta(response.meta);
    postsCache.setCachedPosts(response.data);
  };

  return { 
    posts, 
    loading, 
    error, 
    meta, 
    htmlStatus,
    generateHTML,
    generateAllHTML,
    forceRefresh
  };
};

export const useBlogPost = (slug: string) => {
  const [post, setPost] = useState<StoryChiefPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [htmlGenerated, setHtmlGenerated] = useState(false);
  const [fromCache, setFromCache] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('Fetching post with slug:', slug);
        
        // First, try to get from cache and check if it's still valid
        const cachedPost = await htmlPersistence.getPostFromHTML(slug);
        
        if (cachedPost) {
          // Validate cache by checking if the post was modified
          try {
            console.log('📦 Validating cached post...');
            const response = await storyChiefAPI.getPosts({ per_page: 100, status: 'published' });
            const currentPost = response.data.find(p => p.slug === slug);
            
            if (currentPost) {
              const cachedModified = cachedPost.updated_at || cachedPost.published_at;
              const currentModified = currentPost.updated_at || currentPost.published_at;
              
              if (cachedModified && currentModified) {
                const cachedDate = new Date(cachedModified);
                const currentDate = new Date(currentModified);
                
                if (cachedDate >= currentDate) {
                  console.log(`✅ Using cached post data for: ${slug} (content unchanged)`);
                  setPost(cachedPost);
                  setHtmlGenerated(true);
                  setFromCache(true);
                  return;
                } else {
                  console.log(`📦 Cached post for ${slug} is outdated, fetching fresh data`);
                }
              }
            }
          } catch (validationError) {
            console.warn('Could not validate cache, using cached version:', validationError);
            // Use cached version if validation fails
            setPost(cachedPost);
            setHtmlGenerated(true);
            setFromCache(true);
            return;
          }
        }

        // If not in cache or stale, fetch from API
        console.log(`🔄 Fetching fresh data from API for: ${slug}`);
        const response = await storyChiefAPI.getPostBySlug(slug);
        setPost(response);
        setFromCache(false);
        
        // Automatically generate HTML file when post is fetched from API
        try {
          await htmlPersistence.savePostHTML(response);
          setHtmlGenerated(true);
          console.log(`💾 Auto-generated HTML for: ${response.title}`);
        } catch (htmlError) {
          console.error('Failed to generate HTML:', htmlError);
          // Don't fail the whole request if HTML generation fails
        }
        
      } catch (err) {
        console.error('Error fetching post:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch post');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPost();
    }
  }, [slug]);

  return { post, loading, error, htmlGenerated, fromCache };
};

interface HTMLFileInfo {
  filename: string;
  slug: string;
  exists: boolean;
  size: number;
  modified: string;
}

export const useHTMLStatus = () => {
  const [files, setFiles] = useState<HTMLFileInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchHTMLFiles = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/html-files');
      
      if (!response.ok) {
        throw new Error('Failed to fetch HTML files');
      }
      
      const data = await response.json() as { files: HTMLFileInfo[] };
      setFiles(data.files);
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch HTML files');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHTMLFiles();
  }, []);

  const generateSitemap = async (posts: StoryChiefPost[]) => {
    try {
      const response = await fetch('/api/generate-sitemap', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ posts })
      });

      if (!response.ok) {
        throw new Error('Failed to generate sitemap');
      }

      const result = await response.json() as Record<string, unknown>;
      console.log('Sitemap generated successfully');
      return result;
      
    } catch (error) {
      console.error('Error generating sitemap:', error);
      throw error;
    }
  };

  const cleanCache = async (maxAgeHours = 24) => {
    try {
      const response = await fetch('/api/clean-cache', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ maxAgeHours })
      });

      if (!response.ok) {
        throw new Error('Failed to clean cache');
      }

      const result = await response.json() as Record<string, unknown>;
      console.log('Cache cleaned successfully');
      
      // Also clear the posts cache when cleaning
      postsCache.clearCache();
      
      return result;
      
    } catch (error) {
      console.error('Error cleaning cache:', error);
      throw error;
    }
  };

  return { 
    files, 
    loading, 
    error, 
    refetch: fetchHTMLFiles,
    generateSitemap,
    cleanCache
  };
};